import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Menu, X, ArrowUpRight } from "lucide-react";

const links = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/booking", label: "Booking" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const nav = useNavigate();
  const loc = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [loc.pathname]);

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-500 border-b h-20 flex items-center ${
        scrolled
          ? "bg-[#283828]/85 backdrop-blur-md border-[#3a4d3a]/60 shadow-lg"
          : "bg-[#283828] border-transparent"
      }`}
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 w-full flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center group pl-2" data-testid="nav-logo">
          <span
            className="text-[2rem] md:text-[2.2rem] leading-none tracking-tight"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}
          >
            <span className="text-[#F4F1E8]">Dobhai</span>
            <span className="text-[#C9D1B5]">Official</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8 lg:gap-10">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `nav-link font-sans text-[13px] tracking-[0.18em] uppercase font-semibold transition-colors ${
                  isActive ? "is-active text-white" : "text-[#C9D1B5] hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-5">
          {user ? (
            <>
              <Link
                to={user.role === "admin" ? "/admin" : "/dashboard"}
                className="text-[12px] tracking-[0.22em] uppercase font-semibold text-[#C9D1B5] hover:text-white transition-colors"
                data-testid="nav-dashboard"
              >
                {user.role === "admin" ? "Admin" : "My Portal"}
              </Link>
              <button
                onClick={async () => { await logout(); nav("/"); }}
                className="text-[12px] tracking-[0.22em] uppercase font-medium text-[#C9D1B5]/70 hover:text-white transition-colors"
                data-testid="nav-logout"
              >
                Sign out
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                className="text-[12px] tracking-[0.22em] uppercase font-semibold text-[#C9D1B5] hover:text-white transition-colors"
                data-testid="nav-login"
              >
                Sign in
              </Link>
              <Link to="/booking" className="btn-premium py-3 px-5 text-xs" data-testid="nav-book">
                Book Session <ArrowUpRight size={14} strokeWidth={1.6} className="ml-2" />
              </Link>
            </>
          )}
        </div>

        <button
          className="md:hidden p-2 text-white"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} strokeWidth={1.5} /> : <Menu size={24} strokeWidth={1.5} />}
        </button>
      </div>

      {open && (
        <div className="absolute top-full left-0 right-0 md:hidden border-t border-[#3a4d3a] bg-[#283828]/95 backdrop-blur-md shadow-2xl" data-testid="nav-mobile-panel">
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-2">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `text-base font-semibold tracking-[0.16em] uppercase py-3 border-b border-[#3a4d3a] ${
                    isActive ? "text-white" : "text-[#C9D1B5]"
                  }`
                }
                data-testid={`nav-mobile-${l.label.toLowerCase()}`}
              >
                {l.label}
              </NavLink>
            ))}
            <div className="pt-5 flex flex-col gap-3">
              {user ? (
                <>
                  <Link
                    to={user.role === "admin" ? "/admin" : "/dashboard"}
                    className="text-[#C9D1B5] uppercase tracking-[0.2em] text-sm font-semibold"
                  >
                    {user.role === "admin" ? "Admin" : "My Portal"}
                  </Link>
                  <button
                    onClick={async () => { await logout(); nav("/"); }}
                    className="text-left text-[#C9D1B5]/70 uppercase tracking-[0.2em] text-sm"
                  >
                    Sign out
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="text-[#C9D1B5] uppercase tracking-[0.2em] text-sm font-semibold">
                    Sign in
                  </Link>
                  <Link to="/booking" className="btn-premium w-full justify-center py-3 text-xs mt-2">
                    Book Session <ArrowUpRight size={14} strokeWidth={1.6} className="ml-2" />
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}